import { Component, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationsHub } from '../hubs/notifications.hub';
import { ToastService } from '../../../../trash/shared/components/toasters/Toast.service';
import { INotificationMessage, NotificationMessage } from '../models/notification.message.model';
import { Logger } from '../../../core/utilities/logging/logger';
import { NotificationService } from '../services/notifications.service';
import { NotificationGroupssClientProvider as NotificationGroupsClientProvider } from '../../notification-management/notification-groups/clients/notification-groups.client.provider';
import { INotificationGroupsClient } from '../../notification-management/notification-groups/clients/i-notification-groups.client';
import { UserContextService } from '../../../user-management/auth-management/authentication/services/user-context.service';
import { handleApiErrors } from '../../../core/data-access/http-client/interceptors/error.interceptor';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, NgbTooltipModule, RouterModule, TranslateModule],
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit {
  private readonly NOTIFICATION_SOUND_PATH = '../../../../../assets/audio/notifications/pop-up-alert-notification.wav';
  notificationService = inject(NotificationService);

  audio!: HTMLAudioElement;
  static readonly processedMessaged: Set<string> = new Set();
  protected content = signal([]);
  showUi = input<boolean>(true);
  notificationsLimit = input<number>();
  activeTab: 'all' | 'unread' = 'all';

  notifications$ = this.notificationService.notifications;
  unReadNotifications$ = this.notificationService.unReadNotifications;
  unreadCount$ = this.notificationService.unreadCount;

  earlierNotifications = this.notifications$();
  newNotifications = this.notifications$();

  client!: INotificationGroupsClient;

  constructor(
    protected notificationsHub: NotificationsHub,
    public toastService: ToastService,
    private clientProvider: NotificationGroupsClientProvider
  ) {}

  ngOnInit(): void {
    this.client = this.clientProvider.getClient();

    this.initializeAudio();

    // History and the badge come from the server; the hub only adds what arrives while the page is open.
    this.notificationService.refresh();

    this.notificationsHub.messageReceived$.subscribe({
      next: (x) => this.handleMessageReceived(x),
      error: this.handleMessageReceivedFailure
    });

    this.notificationsHub.connectionStatus$.subscribe({
      next: (x) => this.handleConnectionStatusChange(x, this.client)
    });
    Logger.debug(this.notifications$());
  }

  protected joinGroups(groups: string[]): void {
    this.notificationsHub.joinGroups(groups).subscribe({
      next: (_) => Logger.info(`successfully joined groups`, groups),
      error: (_) => Logger.warn(`Error joining groups`, groups)
    });
  }

  protected leaveGroups(groups: string[]): void {
    this.notificationsHub.leaveGroups(groups).subscribe({
      next: (_) => Logger.info(`successfully left groups`, groups),
      error: (_) => Logger.warn(`Error leaving groups`, groups)
    });
  }

  handleMessageReceived(message: INotificationMessage<any>) {
    if (NotificationsComponent.processedMessaged.has(message.id)) {
      return;
    }

    this.playNotificationSound();

    NotificationsComponent.processedMessaged.add(message.id);

    Logger.info('Notification Message Received', message);

    this.notificationService.addNotification(message);
  }

  handleMessageReceivedFailure() {
    Logger.error('Error has been occured  while recieving notification message');
  }

  markAllAsRead() {
    this.notificationService.markAllNotificationsAsRead();
  }

  markNotificationAsRead(id: string) {
    this.notificationService.markNotificationAsRead(id);
  }

  clearNotification(id: string) {
    this.notificationService.clearNotification(id);
  }

  clearAllNotifications() {
    this.notificationService.clearAllNotifications();
  }

  setActiveTab(tab: 'all' | 'unread'): void {
    this.activeTab = tab;
  }

  getNotifications(): NotificationMessage<any>[] {
    const notifications = this.activeTab === 'all' ? this.notifications$() : this.unReadNotifications$();

    if (!this.notificationsLimit()) {
      return notifications;
    }

    return notifications.slice(0, this.notificationsLimit());
  }

  private handleConnectionStatusChange(connectionStatus: boolean, client: INotificationGroupsClient) {
    if (!connectionStatus) {
      return;
    }

    const roles = UserContextService.getUserContext()?.systemRoles ?? [];

    client.getByRoles(roles).subscribe({
      next: (groups) => {
        const groupNames = groups.map((group) => group.name);
        this.joinGroups(groupNames);
      },
      error: (x) => handleApiErrors(x, this.toastService)
    });
  }

  handleNotificationClick(notification: NotificationMessage<any>) {
    Logger.debug('Test', notification);
    Logger.debug('Test', notification.getRouteUrl());
  }

  private initializeAudio(): void {
    try {
      this.audio = new Audio(this.NOTIFICATION_SOUND_PATH);
      this.audio.load();
    } catch (error) {
      Logger.error('Failed to initialize audio:', error);
    }
  }

  playNotificationSound(): void {
    try {
      this.audio.currentTime = 0;
      this.audio.play().catch((error) => {
        Logger.warn('Failed to play notification sound:', error);
      });
    } catch (error) {
      Logger.warn('Error initializing notification sound:', error);
    }
  }
}
